import React, {useEffect, useMemo, useRef, useState} from 'react';
import Wrapper from '../../components/Wrapper';
import DashboardHeader from '../../components/DashboardHeader';
import {
  useCompleteInspectionMutation,
  useLazyGetCommentsQuery,
  useLazyGetCurrentInspectionAndCategoryQuery,
  useLazyGetInspectionFacilityByIdQuery,
  useSkipInspectionMutation,
} from '../../store/builder/inspections.api';
import {
  clearGravityFields,
  setFields,
  setLoader,
} from '../../store/builder/reducer';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import './index.scss';
import Checked from '../../SVG/Checked';
import FIllField from '../../components/FIllField';
import {toast} from 'react-toastify';
import {useErrorHandler} from '../../global/ErrorsHandler';
import {Helmet} from 'react-helmet';
import axios from 'axios';
import CommentsIcon from '../../SVG/CommentsIcon';
import listIcon from '../../SVG/list.svg';
import CommentsInspections from '../../components/CommentsInspections';
import moment from 'moment';
import CompletedInspectionTags from './CompletedInspectionTags';
import HeaderBottom from './HeaderBottom';
import CorrectiveActionsModal from './CorrectiveActionsModal';
import './CorrectiveActionsModal.scss';
import LogsModal from './LogsModal';
import {useTranslation} from '../../hooks/useTranslation';
import {isFlagOn} from '../../utils/flags';
import {
  describeAcceptable,
  isOutsideAcceptable,
  parseAcceptable,
  toNumber,
} from '../../utils/acceptable';
// Translations come from the backend, so a key it does not ship with would
// render as the raw key. These are the fallbacks for the fill_after hint.
const FILL_AFTER_HINT_KEY = 'fillInspection.fillAfter.hint';
const FILL_AFTER_HINT_FALLBACK = {
  en: 'Fill in the following fields first:',
  fr: "Veuillez d'abord remplir les champs suivants :",
};

// `fill_after` holds a comma separated list of field ids that have to be
// answered before the field itself can be filled.
const parseFillAfterIds = value =>
  value === null || value === undefined
    ? []
    : String(value)
        .split(',')
        .map(id => id.trim())
        .filter(Boolean);

// A field carrying `show_oos_warning` takes the equipment out of service when
// its answer falls outside its `acceptable`.
const getOutOfServiceFields = fields =>
  (fields ?? []).filter(field => {
    if (field?.hidden || field?.web_hidden || field?.scale_hidden) {
      return false;
    }
    if (!isFlagOn(field?.show_oos_warning)) {
      return false;
    }
    const bounds = parseAcceptable(field?.acceptable);
    const value = toNumber(field?.answers?.[0]);
    if (!bounds || Number.isNaN(value)) {
      return false;
    }
    return isOutsideAcceptable(value, bounds);
  });

// The backend ships the translations, so a key it does not carry would render
// as the raw key. These are the fallbacks for the out of service warning.
const OOS_FALLBACK = {
  en: {
    title: 'Equipment Will Go Out of Service',
    message:
      'These readings are outside their acceptable range. Completing this inspection will take the equipment out of service.',
    goBack: 'Go back',
    confirm: 'Complete Anyway',
  },
  fr: {
    title: "L'équipement sera mis hors service",
    message:
      "Ces relevés sont en dehors de leur plage acceptable. Terminer cette inspection mettra l'équipement hors service.",
    goBack: 'Retour',
    confirm: 'Terminer quand même',
  },
};

// The category to open after one is saved: the first one still to be filled
// that is not the one just saved, or -1 when there is none left.
const findNextCategoryIndex = (categories, currentIndex) =>
  (categories ?? []).findIndex(
    (item, index) =>
      !item?.is_completed && !item?.is_hidden && index !== currentIndex,
  );

const isFieldFilled = field => {
  const answer = field?.answers?.[0];
  return (
    field?.answers?.length > 0 &&
    answer !== null &&
    answer !== undefined &&
    String(answer).length > 0
  );
};

// `required_when` is a list of conditions, each naming a field id and the
// exact answer it must have, e.g.
// [{"id": "1", "value": "SFS"}, {"id": "4", "value": "Y"}]. When every
// condition matches, the field becomes required regardless of its `optional`
// flag.
const parseRequiredWhen = value => {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      return parseRequiredWhen(JSON.parse(value));
    } catch {
      return null;
    }
  }
  if (!Array.isArray(value)) return null;
  const conditions = value.filter(
    condition =>
      condition?.id !== null &&
      condition?.id !== undefined &&
      String(condition.id).length > 0,
  );
  return conditions.length > 0 ? conditions : null;
};

const isRequiredWhenMet = (field, fields) => {
  const conditions = parseRequiredWhen(field?.required_when);
  if (!conditions) return false;
  return conditions.every(({id, value: expected}) => {
    const target = fields?.find(el => String(el?.id) === String(id));
    const answer = target?.answers?.[0];
    return (
      answer !== null &&
      answer !== undefined &&
      String(answer) === String(expected)
    );
  });
};

export default function FillInspection() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const {t, language} = useTranslation();
  const [currentItem, setCurrentItem] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [showImages, setShowImages] = useState(null);
  const [modalStatus, setModalStatus] = useState(false);
  const [skipModal, setSkipModal] = useState(false);
  const [lastCategory, setLastCategory] = useState(false);
  const [sameIdsError, setSameIdsError] = useState(false);
  const fields = useSelector(state => state.rootReducer.reducer.fields);
  const fieldsCategoryId = useSelector(
    state => state.rootReducer.reducer.category_id,
  );
  const [localLoaderStatus, setLocalLoaderStatus] = useState(false);
  const [requestSended, setRequestSended] = useState(false);
  const [showCategories, setShowCategories] = useState([]);
  const [commentsStatus, setCommentsStatus] = useState(false);
  const [correctiveActionsModal, setCorrectiveActionsModal] = useState(false);
  const [gravityOutOfRangeModal, setGravityOutOfRangeModal] = useState(false);
  const [oosWarningModal, setOosWarningModal] = useState(false);
  const [oosWarningFields, setOosWarningFields] = useState([]);
  const [fieldsRenderKey, setFieldsRenderKey] = useState(0);
  const [currentCorrectiveActions, setCurrentCorrectiveActions] = useState([]);
  const [correctiveActionStep, setCorrectiveActionStep] = useState(1);
  const [viewedCorrectiveActionIds, setViewedCorrectiveActionIds] = useState(
    new Set(),
  );
  const [logsModal, setLogsModal] = useState(false);
  const [saveAndContinueConfirmModal, setSaveAndContinueConfirmModal] =
    useState(false);
  const [manualCompletionModal, setManualCompletionModal] = useState(false);
  const [manualCompletionDate, setManualCompletionDate] = useState('');
  const [manualCompletionDateError, setManualCompletionDateError] =
    useState('');
  const [manualCompletionComment, setManualCompletionComment] = useState('');
  const [manualCompletionSubmitting, setManualCompletionSubmitting] =
    useState(false);
  const [pendingCategoriesAfterSave, setPendingCategoriesAfterSave] =
    useState(null);
  const [manualCompletionDateRange, setManualCompletionDateRange] = useState({
    start: '',
    end: '',
  });
  const [sendData, setSendData] = useState({
    items: [
      {
        inspection_id: params?.id,
        fields: [],
      },
    ],
  });

  const [startTime, setStartTime] = useState(null);
  const [
    complete,
    {
      isError: completeIsError,
      isFetching: completeIsLoading,
      isSuccess: completeIsSuccess,
      error: completeError,
    },
  ] = useCompleteInspectionMutation();

  const [
    skipInspection,
    {
      data: skipInspectionData,
      isError: skipInspectionIsError,
      isLoading: skipInspectionIsLoading,
      isSuccess: skipInspectionIsSuccess,
      error: skipInspectionError,
    },
  ] = useSkipInspectionMutation();

  const [
    getInspectionAndCategory,
    {
      data: getInspectionAndCategoryData,
      isError: getInspectionAndCategoryIsError,
      isFetching: getInspectionAndCategoryIsLoading,
      isSuccess: getInspectionAndCategoryIsSuccess,
      error: getInspectionAndCategoryError,
    },
  ] = useLazyGetCurrentInspectionAndCategoryQuery();

  const [getInspectionById, {data, isLoading, isError, error, isSuccess}] =
    useLazyGetInspectionFacilityByIdQuery();
    const calculateDisabled = data?.is_complete;
  const [
    getComments,
    {
      data: getCommentsData,
      isError: getCommentsIsError,
      isFetching: getCommentsIsLoading,
      isSuccess: getCommentsIsSuccess,
      error: getCommentsError,
    },
  ] = useLazyGetCommentsQuery();

  const [errorHandler, setErrorHandler] = useErrorHandler([
    error,
    completeError,
    skipInspectionError,
  ]);

  const getCommentsAction = () => {
    getComments(params?.id);
  };

  useEffect(() => {
    if (commentsStatus) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [commentsStatus]);

  useEffect(() => {
    if (
      currentItem &&
      currentItem.corrective_actions &&
      currentItem.corrective_actions.length > 0
    ) {
      // Check if any of the corrective actions haven't been viewed yet
      const hasUnviewedActions = currentItem.corrective_actions.some(
        action => !viewedCorrectiveActionIds.has(action.id),
      );

      if (hasUnviewedActions && !currentItem?.is_completed) {
        setCurrentCorrectiveActions(currentItem.corrective_actions);
        setCorrectiveActionsModal(true);
      }
    }
  }, [currentItem, viewedCorrectiveActionIds]);

  useEffect(() => {
    if (isSuccess) {
      if (showCategories?.length > 0) {
        // When categories already exist in state, fully replace them with
        // the latest backend categories, including backend is_hidden flags.
        setShowCategories(JSON.parse(JSON.stringify(data?.categories ?? [])));
      } else {
        let categoriesArr = JSON.parse(JSON.stringify([...data?.categories]));
        setShowCategories(() => {
          let parentCategory = categoriesArr?.filter(item => {
            let parentField = item?.fields?.filter(el => {
              if (el.hasOwnProperty('child_category')) {
                return el;
              }
            });
            if (parentField?.length > 0) {
              return item;
            }
          });
          if (parentCategory?.length > 0) {
            if (parentCategory[0]?.is_completed) {
              return categoriesArr?.map(category => {
                parentCategory[0]?.fields?.map(field => {
                  if (field?.hasOwnProperty('child_category')) {
                    if (field?.answers[0] === field?.child_category?.answer) {
                      field.child_category.code?.map(code => {
                        if (category.category_code === code) {
                          category.is_hidden = false;
                        }
                        return code;
                      });
                    }
                  }
                  return field;
                });
                return category;
              });
            }
          }
          return categoriesArr;
        });
      }
    }
  }, [isSuccess, data]);

  useEffect(() => {
    if (skipInspectionIsLoading) {
      dispatch(setLoader(true));
    } else {
      dispatch(setLoader(false));
    }
    if (skipInspectionIsSuccess) {
      dispatch(setLoader(false));
      setSkipModal(false);
      toast.success('Inspection has been successfully skipped');
      navigate('/inspections');
    }
    if (skipInspectionIsError) {
      dispatch(setLoader(false));
    }
  }, [skipInspectionIsLoading, skipInspectionIsSuccess, skipInspectionIsError]);

  useEffect(() => {
    getCommentsAction();
  }, []);

  // Field ids only have to be unique inside a category, so the store's fields
  // may not be matched against another category's by id. While a switch is in
  // flight the store still holds the category the user is leaving, and until it
  // catches up the category is rendered from its own fields.
  const categoryFields =
    currentItem?.id !== undefined && fieldsCategoryId === currentItem?.id
      ? fields
      : null;

  // Only reset and re-fetch when inspection ID actually changes (e.g. navigation), not on first mount or when selecting another category
  const prevInspectionIdRef = useRef(undefined);
  useEffect(() => {
    const currentId = params?.id;
    if (
      prevInspectionIdRef.current !== undefined &&
      prevInspectionIdRef.current !== currentId
    ) {
      setRequestSended(false);
    }
    prevInspectionIdRef.current = currentId;
  }, [params?.id]);

  // If user selects a category before initial fetch completes, don't overwrite with first category
  const userSelectedCategoryRef = useRef(false);
  // When refetching after corrective actions modal close, keep current category instead of jumping to first
  const preserveCategoryOnNextFetchRef = useRef(false);
  const preservedCategoryIdRef = useRef(null);

  useEffect(() => {
    if (!requestSended) {
      const isRefetchPreserveCategory = preserveCategoryOnNextFetchRef.current;
      if (isRefetchPreserveCategory) {
        preserveCategoryOnNextFetchRef.current = false;
        preservedCategoryIdRef.current = currentItem?.id ?? null;
        // Keep userSelectedCategoryRef true so we don't set first category when fetch completes
      } else {
        userSelectedCategoryRef.current = false;
      }

      getInspectionById(params?.id)
        .unwrap()
        .then(res => {
          dispatch(setLoader(false));
          setLocalLoaderStatus(false);
          setRequestSended(true);

          const categories = res?.categories ?? [];
          const neededCategoryIdx =
            categories.findIndex(c => !c?.is_hidden) ?? -1;
          const firstCategory =
            neededCategoryIdx >= 0 ? categories[neededCategoryIdx] : null;

          if (firstCategory) {
            setStartTime(new Date());
            const preservedId = preservedCategoryIdRef.current;
            const preservedCategory =
              preservedId != null
                ? categories.find(c => c?.id === preservedId)
                : null;

            if (preservedCategory) {
              // Refetch after modal close: keep current category and do NOT reset field values
              preservedCategoryIdRef.current = null;
              // Don't set currentItem/setFields/sendData - leave user's answers and corrective action updates as is
            } else if (!userSelectedCategoryRef.current) {
              setCurrentItem(firstCategory);
              setCurrentIndex(neededCategoryIdx);
              setSendData(old => ({
                items: [
                  {
                    ...old.items[0],
                    category_id: firstCategory?.id,
                  },
                ],
              }));
              dispatch(
                setFields({
                  fields: (firstCategory?.fields ?? []).filter(el => {
                    if (el?.data_type === 'flow_rate' && el?.hidden)
                      return true;
                    return !el?.hidden;
                  }),
                  type: 'full',
                  category_id: firstCategory?.id,
                }),
              );
            }
          } else if (!userSelectedCategoryRef.current) {
            setCurrentItem(null);
            setCurrentIndex(null);
          }
        })
        .catch(err => {
          dispatch(setLoader(false));
          setRequestSended(true);
        });
    }
  }, [params?.id, requestSended]);

  useEffect(() => {
    if (currentItem) {
      setSendData(old => {
        return {
          items: [
            {
              ...old.items[0],
              category_id: currentItem?.id,
              fields: currentItem?.fields?.map(item2 => {
                const matchingItem = categoryFields?.find(
                  item1 => item1.id === item2.id,
                );
                if (matchingItem) {
                  return {
                    ...item2,
                    answers: item2?.web_hidden ? [] : matchingItem.answers,
                    comment_files: matchingItem.comment_files,
                    comment_text: matchingItem.comment_text,
                    ...(matchingItem.photo !== undefined && {
                      photo: matchingItem.photo,
                    }),
                    ...(matchingItem.photos !== undefined && {
                      photos: matchingItem.photos,
                    }),
                  };
                }
                return item2;
              }),
            },
          ],
        };
      });
    }
  }, [currentItem, categoryFields]);

  useEffect(() => {
    if (currentItem) {
      if (
        data?.categories[data?.categories.length - 1]?.id === currentItem?.id
      ) {
        setLastCategory(true);
      } else {
        setLastCategory(false);
      }
    }
  }, [currentItem]);

  useEffect(() => {
    let hasDuplicate = false;
    for (let i = 0; i < currentItem?.fields?.length; i++) {
      for (let j = i + 1; j < currentItem?.fields?.length; j++) {
        if (currentItem?.fields[i]?.id === currentItem?.fields[j]?.id) {
          hasDuplicate = true;
          break;
        }
      }
      if (hasDuplicate) break;
    }
    setSameIdsError(hasDuplicate);
  }, [currentItem]);

  // useEffect(() => {
  //   setCurrentItem(old => {
  //     return {
  //       ...old,
  //       fields: fields,
  //     };
  //   });
  // }, [fields]);

  useEffect(() => {
    if (
      currentItem?.fields?.filter(el => el.children_optional_when)?.length > 0
    ) {
      categoryFields?.forEach(parentField => {
        if (parentField.hasOwnProperty('children_optional_when')) {
          let parentFieldCheck =
            parentField?.children_optional_when?.split(',');
          if (
            parentFieldCheck?.filter(el => el === parentField?.answers[0])[0]
          ) {
            setCurrentItem(old => {
              // If for some reason currentItem was cleared, don't try to update it
              if (!old || !old.fields) {
                return old;
              }
              return {
                ...old,
                fields: old.fields.map(field => {
                  if (!field?.hidden) {
                    if (field.hasOwnProperty('parent_id')) {
                      if (field.parent_id === parentField?.id) {
                        if (field?.tooltip?.toLowerCase() !== 'optional') {
                          return {
                            ...field,
                            optional: true,
                            web_hidden: true,
                            answers: [],
                          };
                        } else {
                          return {...field, web_hidden: true};
                        }
                      } else {
                        return field;
                      }
                    } else {
                      return field;
                    }
                  } else {
                    return field;
                  }
                }),
              };
            });
          } else {
            setCurrentItem(old => {
              // If for some reason currentItem was cleared, don't try to update it
              if (!old || !old.fields) {
                return old;
              }
              return {
                ...old,
                fields: old.fields.map(field => {
                  if (!field?.hidden) {
                    if (field.hasOwnProperty('parent_id')) {
                      if (field.parent_id === parentField?.id) {
                        if (field?.tooltip?.toLowerCase() !== 'optional') {
                          return {
                            ...field,
                            optional: false,
                            web_hidden: false,
                          };
                        } else {
                          return {...field, web_hidden: false};
                        }
                      } else {
                        return field;
                      }
                    } else {
                      return field;
                    }
                  } else {
                    return field;
                  }
                }),
              };
            });
          }
        }
      });
    } else if (categoryFields) {
      setSendData(old => ({
        items: [
          {
            ...old.items[0],
            category_id: currentItem?.id,
            fields: categoryFields,
          },
        ],
      }));
    }
  }, [categoryFields, currentIndex]);

  const handleItem = (item, index) => {
    userSelectedCategoryRef.current = true;
    setStartTime(new Date());
    setCurrentItem(null);
    setCurrentIndex(null);
    // Clear viewed corrective actions when switching categories
    setViewedCorrectiveActionIds(new Set());
    setTimeout(() => {
      setCurrentItem(item);
      setCurrentIndex(index);
      setSendData(old => ({
        items: [{...old.items[0], category_id: item?.id, fields: []}],
      }));
      dispatch(
        setFields({fields: item?.fields, type: 'full', category_id: item?.id}),
      );
    }, 0);
  };

  const handleFile = e => {
    const selectedFiles = e.target.files;
    const selectedFilesArray = Array.from(selectedFiles);

    const imagesArray = selectedFilesArray.map(file => {
      return URL.createObjectURL(file);
    });

    setShowImages(imagesArray);
  };

  const renderCategories = () => {
    if (location.search) {
      return (
        <div
          key={getInspectionAndCategoryData?.categories?.id}
          className={`fillInspection__item fillInspection__item_active`}
        >
          <div className='fillInspection__item-title'>
            {getInspectionAndCategoryData?.categories?.label}
          </div>
          {getInspectionAndCategoryData?.categories?.is_completed && (
            <>
              <div className='fillInspection__item-text'>
                Submitted by:{' '}
                {getInspectionAndCategoryData?.categories?.submitted_by}
              </div>
              <div className='fillInspection__item-text'>
                {moment(
                  getInspectionAndCategoryData?.categories?.completed_at,
                ).format('MM/DD/YYYY HH:mm')}
              </div>
            </>
          )}
          <div className='fillInspection__item-icon'>
            <Checked colorBg={'#FFFFFF'} colorCheck={'#4268B0'} />
          </div>
        </div>
      );
    } else {
      return showCategories?.map((item, index) => {
        const filteredCategoryFields = item?.fields?.filter(el => el.hidden);
        const hasCorrectiveActions =
          item.corrective_actions && item.corrective_actions.length > 0;

        if (filteredCategoryFields?.length !== item?.fields?.length) {
          if (!item?.is_hidden) {
            return (
              <div
                onClick={() => {
                  window.scroll({top: 0, behavior: 'smooth'});
                  handleItem(item, index);
                }}
                key={item?.uid}
                className={`fillInspection__item ${
                  currentItem?.id === item?.id
                    ? 'fillInspection__item_active'
                    : ''
                } ${item?.is_completed ? 'fillInspection__item_completed' : ''}`}
              >
                <div className='fillInspection__item-box'>
                  <div className='fillInspection__item-title'>
                    {item?.label}
                  </div>
                  {item?.is_completed && (
                    <>
                      <div className='fillInspection__item-text'>
                        {t('fillInspection.submittedBy')}:{' '}
                        <span>{item?.submitted_by}</span>
                      </div>
                      {item?.old_completed_date && (
                        <div
                          className='fillInspection__item-text'
                          style={{
                            textDecoration: 'line-through',
                            color: '#9CA3AF',
                          }}
                        >
                          <span>
                            {moment(item?.old_completed_date).format(
                              'MM/DD/YYYY HH:mm',
                            )}
                          </span>
                        </div>
                      )}
                      <div className='fillInspection__item-text'>
                        <span>
                          {moment(item?.completed_at).format(
                            'MM/DD/YYYY HH:mm',
                          )}
                        </span>
                      </div>
                      <div className='fillInspection__item-tags'>
                        {
                          <CompletedInspectionTags
                            item={item}
                            showNfc={
                              !!data?.beacon_address?.length && item?.is_mobile
                            }
                          />
                        }
                      </div>
                    </>
                  )}
                  {/*<div className="fillInspection__item-nfcContainer">*/}
                  {/*  <div className="fillInspection__item-nfcCheckbox nfc-inactive">*/}
                  {/*    <WiFiIcon color={'#425433'}/>*/}
                  {/*    <NfcTextIcon />*/}
                  {/*  </div>*/}
                  {/*  <div className="fillInspection__item-nfcCheckbox">*/}
                  {/*    <GlobalIcon />*/}
                  {/*    <WebTextIcon />*/}
                  {/*  </div>*/}
                  {/*</div>*/}
                </div>

                <div className='fillInspection__item-icon'>
                  <Checked
                    colorBg={
                      currentItem?.id === item?.id && item?.is_completed
                        ? '#82C971'
                        : currentItem?.id === item?.id
                          ? '#FFFFFF'
                          : item?.is_completed
                            ? '#82C971'
                            : '#AEB3B7'
                    }
                    colorCheck={
                      currentItem?.id === item?.id && item?.is_completed
                        ? '#FFF'
                        : currentItem?.id === item?.id
                          ? '#4268B0'
                          : item?.is_completed
                            ? '#FFF'
                            : '#FFF'
                    }
                  />
                </div>
              </div>
            );
          }
        } else {
          return null;
        }
      });
    }
  };
  const renderImages = () => {
    return showImages?.map((item, index) => (
      <div key={index} className='fillInspection__field-commentImage'>
        <div className='fillInspection__field-commentImageDel'>
          <img src='/assets/icons/trash2.svg' alt='trash' />
        </div>
        <img className='fillInspection__field-commentImg' src={item} />
      </div>
    ));
  };

  const renderFields = () => {
    const translatedFillAfterHint = t(FILL_AFTER_HINT_KEY);
    const fillAfterHint =
      !translatedFillAfterHint || translatedFillAfterHint === FILL_AFTER_HINT_KEY
        ? (FILL_AFTER_HINT_FALLBACK[language] ?? FILL_AFTER_HINT_FALLBACK.en)
        : translatedFillAfterHint;

    const preparedFields = currentItem?.fields
      ?.map(item => {
        if (item?.hasOwnProperty('parent_id')) {
          if (item?.hasOwnProperty('optional')) {
            return item;
          } else {
            return {...item, optional: true};
          }
        } else {
          return item;
        }
      })
      ?.map(field => {
        const fromRedux = categoryFields?.find(f => f?.id === field?.id);
        return fromRedux ? {...field, ...fromRedux} : field;
      });

    // Fields listed in `fill_after` that still have no answer. Ids that are not
    // present in the category are ignored so an unknown id never locks a field.
    const getMissingFillAfterFields = field =>
      parseFillAfterIds(field?.fill_after)
        .map(id => preparedFields?.find(el => String(el?.id) === id))
        .filter(
          el =>
            el &&
            !el?.hidden &&
            !el?.web_hidden &&
            !el?.scale_hidden &&
            !isFieldFilled(el),
        );

    return preparedFields?.map(item => {
      // `scale_hidden` is the reading the selected deice tool does not measure.
      if (item?.hidden || item?.web_hidden || item?.scale_hidden) {
        return null;
      }
      const missingFillAfterFields = data?.is_complete
        ? []
        : getMissingFillAfterFields(item);
      const lockedByFillAfter = missingFillAfterFields.length > 0;
      return (
        <div key={item?.id} className='fillInspection__fillAfter'>
          <div
            className={
              lockedByFillAfter ? 'fillInspection__fillAfter-locked' : ''
            }
          >
            <FIllField
              calculateDisabled={calculateDisabled}
              sendData={sendData}
              setSendData={setSendData}
              item={item}
              handleFile={handleFile}
              renderImages={renderImages}
              setCurrentItem={setCurrentItem}
              currentItem={currentItem}
              sheet={location?.state?.sheet}
              inspection_id={params?.id}
              readOnly={lockedByFillAfter || location?.state?.readOnly}
              data={data}
              correctiveActionsModal={correctiveActionsModal}
            />
          </div>
          {lockedByFillAfter && (
            <p className='fillInspection__fillAfter-hint'>
              {fillAfterHint}{' '}
              {missingFillAfterFields.map(el => el?.label).join(', ')}
            </p>
          )}
        </div>
      );
    });
  };

  const checkFsi = () => {
    let fsi;
    fsi = sendData?.items[0]?.fields?.some(
      item =>
        item?.data_type === 'fsii_available' && item?.answers[0] === 'N/A',
    );
    if (fsi) {
      sendData.items[0].fields = sendData?.items[0]?.fields?.map(field => {
        if (
          field?.data_type !== 'fsii_available' &&
          field.data_type?.includes('fsii')
        ) {
          return {...field, optional: true};
        }
        return field;
      });
    }
  };

  const getInspectionRequestHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('ndx_user_token')}`,
    time: String(Date.now()),
    agency: localStorage.getItem('current-fbo'),
    DeviceType: 'web',
    AppVersion: '1.0.0',
    AppType: 'web',
  });

  const parseDateTime = value => {
    if (!value) return '';
    const parsed = moment.utc(
      value,
      [
        moment.ISO_8601,
        'YYYY-MM-DD HH:mm:ss',
        'YYYY-MM-DD HH:mm',
        'YYYY-MM-DDTHH:mm:ss',
        'YYYY-MM-DDTHH:mm',
        'YYYY-MM-DD',
      ],
      true,
    );

    if (parsed.isValid()) return parsed;

    return moment.utc(value);
  };

  const toInputDateTime = value => {
    if (!value) return '';
    const parsed = parseDateTime(value);
    return parsed.isValid() ? parsed.format('YYYY-MM-DDTHH:mm') : '';
  };

  const formatBackendDateTime = value => {
    if (!value) return '';
    const parsed = moment.utc(value, 'YYYY-MM-DDTHH:mm', true);
    return parsed.isValid() ? parsed.format('YYYY-MM-DDTHH:mm:ss[Z]') : '';
  };

  const manualCompletionMinDate = useMemo(() => {
    return (
      manualCompletionDateRange.start ||
      toInputDateTime(data?.grace_period_submit_datetime_start) ||
      toInputDateTime(data?.min_manual_inspection_date) ||
      toInputDateTime(data?.date) ||
      ''
    );
  }, [data, manualCompletionDateRange.start]);

  const manualCompletionMaxDate = useMemo(() => {
    return (
      manualCompletionDateRange.end ||
      toInputDateTime(data?.grace_period_submit_datetime_end) ||
      toInputDateTime(data?.max_manual_inspection_date) ||
      toInputDateTime(data?.grace_period_date) ||
      moment().format('YYYY-MM-DDTHH:mm')
    );
  }, [data, manualCompletionDateRange.end]);

  const clampInputDateTime = (value, min, max) => {
    if (!value) return min || max || '';
    if (min && moment(value).isBefore(moment(min))) return min;
    if (max && moment(value).isAfter(moment(max))) return max;
    return value;
  };

  const formatInputDateTimeForDisplay = value => {
    if (!value) return '';
    const parsed = moment(value, 'YYYY-MM-DDTHH:mm', true);
    return parsed.isValid() ? parsed.format('MMM D, YYYY h:mm A') : value;
  };

  const manualCompletionDateRangeMessage = useMemo(() => {
    const minDate = formatInputDateTimeForDisplay(manualCompletionMinDate);
    const maxDate = formatInputDateTimeForDisplay(manualCompletionMaxDate);

    if (minDate && maxDate) {
      return t('fillInspection.manualCompletion.dateRange.between', { minDate, maxDate });
    }
    if (minDate) return t('fillInspection.manualCompletion.dateRange.after', { minDate });
    if (maxDate) return t('fillInspection.manualCompletion.dateRange.before', { maxDate });
    return '';
  }, [manualCompletionMaxDate, manualCompletionMinDate, t]);

  const getManualCompletionDateError = value => {
    if (!value) {
      return t('fillInspection.manualCompletion.validation.dateRequired');
    }
    if (
      manualCompletionMinDate &&
      moment(value).isBefore(moment(manualCompletionMinDate))
    ) {
      return t('fillInspection.manualCompletion.validation.dateOutOfRange');
    }
    if (
      manualCompletionMaxDate &&
      moment(value).isAfter(moment(manualCompletionMaxDate))
    ) {
      return t('fillInspection.manualCompletion.validation.dateOutOfRange');
    }
    return '';
  };

  const onManualCompletionDateChange = value => {
    setManualCompletionDate(value);
    setManualCompletionDateError(value ? getManualCompletionDateError(value) : '');
  };

  const parseBooleanLike = value => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true' || normalized === '1') return true;
      if (normalized === 'false' || normalized === '0' || normalized === '')
        return false;
    }
    return false;
  };

  const extractGracePeriodSubmitFlag = payload => {
    if (payload == null) return false;

    if (Array.isArray(payload)) {
      return payload.some(item => extractGracePeriodSubmitFlag(item));
    }

    if (typeof payload === 'object') {
      if (
        Object.prototype.hasOwnProperty.call(payload, 'is_grace_period_submit')
      ) {
        return parseBooleanLike(payload.is_grace_period_submit);
      }

      return Object.values(payload).some(value =>
        extractGracePeriodSubmitFlag(value),
      );
    }

    return false;
  };

  const extractGracePeriodSubmitDateRange = payload => {
    const range = {start: '', end: ''};

    const collectRange = value => {
      if (!value || (range.start && range.end)) return;

      if (Array.isArray(value)) {
        value.forEach(collectRange);
        return;
      }

      if (typeof value !== 'object') return;

      if (
        !range.start &&
        Object.prototype.hasOwnProperty.call(
          value,
          'grace_period_submit_datetime_start',
        )
      ) {
        range.start = toInputDateTime(value.grace_period_submit_datetime_start);
      }

      if (
        !range.end &&
        Object.prototype.hasOwnProperty.call(
          value,
          'grace_period_submit_datetime_end',
        )
      ) {
        range.end = toInputDateTime(value.grace_period_submit_datetime_end);
      }

      Object.values(value).forEach(collectRange);
    };

    collectRange(payload);

    return range.start || range.end ? range : null;
  };

  const continueAfterSave = categories => {
    // -1 when nothing is left to fill. The check this replaces read index 0 as
    // "nothing found", so saving the last category while only the first one was
    // left could leave the page on no category at all, showing another
    // category's fields under the new heading.
    const nextIndex = findNextCategoryIndex(categories, currentIndex);

    if (nextIndex >= 0) {
      const nextCategory = categories[nextIndex];
      setCurrentItem(null);
      setCurrentIndex(null);
      setTimeout(() => {
        setCurrentItem(nextCategory);
        setCurrentIndex(nextIndex);
        setSendData(old => ({
          items: [
            {
              ...old.items[0],
              category_id: nextCategory?.id,
              fields: [],
            },
          ],
        }));
        dispatch(
          setFields({
            fields: nextCategory?.fields?.filter(el => {
              if (el.data_type === 'flow_rate' && el.hidden) {
                return el;
              } else {
                return !el.hidden;
              }
            }),
            type: 'full',
            category_id: nextCategory?.id,
          }),
        );
        dispatch(setLoader(false));
      }, 0);
    } else {
      dispatch(setLoader(false));
      setTimeout(() => {
        if (location.search) {
          setModalStatus(true);
        } else {
          navigate(-1);
        }
      }, 1000);
    }
  };

  const submitManualCompletedDate = async () => {
    const trimmedComment = manualCompletionComment.trim();
    const dateError = getManualCompletionDateError(manualCompletionDate);
    if (dateError) {
      setManualCompletionDateError(dateError);
      toast.error(dateError);
      return;
    }
    if (!trimmedComment) {
      toast.error(t('fillInspection.manualCompletion.validation.commentRequired'));
      return;
    }
    if (!pendingCategoriesAfterSave) {
      setManualCompletionModal(false);
      return;
    }

    setManualCompletionSubmitting(true);
    dispatch(setLoader(true));
    try {
      const formattedManualCompletionDate =
        formatBackendDateTime(manualCompletionDate);
      const res = await axios.post(
        `${import.meta.env.VITE_API_URL}inspection/set-manual-completed-date`,
        {
          inspection_id: params?.id,
          custom_completion_date: formattedManualCompletionDate,
          comment: trimmedComment,
        },
        {headers: getInspectionRequestHeaders()},
      );

      if (res?.data?.message) {
        toast.success(res.data.message);
      }

      setManualCompletionModal(false);
      setManualCompletionDate('');
      setManualCompletionDateError('');
      setManualCompletionComment('');
      setManualCompletionDateRange({start: '', end: ''});
      const categories = pendingCategoriesAfterSave;
      setPendingCategoriesAfterSave(null);
      continueAfterSave(categories);
    } catch (err) {
      const apiMessage = err?.response?.data?.message || err?.response?.data?.error;
      const apiError =
        apiMessage === 'messages.inspection_completed_date_not_updated_due_to_wrong_date'
          ? t('fillInspection.manualCompletion.validation.wrongDate')
          : apiMessage || t('fillInspection.manualCompletion.validation.submitFailed');
      toast.error(apiError);
    } finally {
      dispatch(setLoader(false));
      setManualCompletionSubmitting(false);
    }
  };

  const completeInspection = async (options = {}) => {
    const {skipGravityValidation = false, skipOosValidation = false} = options;
    let s = [];
    let a = [];
    let skipValidation;
    skipValidation = sendData?.items[0]?.fields?.some(
      item =>
        (item?.data_type === 'dp_available' ||
          item?.data_type === 'corrected_dp_available') &&
        item?.answers[0] === 'N/A',
    );

    if (!skipValidation) {
      checkFsi();
      const sendFields = sendData?.items[0]?.fields;
      // `scale_hidden` is set on the store's fields, and `sendData` is built
      // from the fields the backend sent, so the ids come from the store.
      const scaleHiddenIds = new Set(
        (categoryFields ?? [])
          .filter(field => field?.scale_hidden)
          .map(f => f?.id),
      );
      sendFields?.forEach((field, index) => {
        if (scaleHiddenIds.has(field?.id)) {
          return;
        }
        const isRequired =
          field?.optional === false ||
          field?.optional === undefined ||
          isRequiredWhenMet(field, sendFields);
        if (
          field?.type === 'sump_sample' ||
          field?.type === 'sump_sample_optional'
        ) {
          if (isRequired) {
            if (field?.answers.length === 0) {
              toast.error(field?.label + ' required');
              s.push(false);
            } else {
              if (field?.answers[0]?.length > 1) {
                a.push(field?.label);
              } else {
                toast.error(field?.label + ' required');
                s.push(false);
              }
            }
          }
        } else if (field?.type === 'dry_rating') {
          if (!field?.optional) {
            if (field?.required) {
              toast.error(
                field?.label +
                  "'s Colorimetric Scale and Colorimetric Graduation fields are required",
              );
              s.push(false);
            } else {
              a.push(field?.label);
            }
          } else {
            a.push(field?.label);
          }
        } else if (field?.data_type === 'fsii_percentage') {
          if (!field?.hidden) {
            if (
              sendData?.items[0]?.fields[index - 1].answers[0] === 'Y' &&
              !field.answers.length
            ) {
              toast.error('FSII Concentration value is required');
              s.push(false);
            } else {
              if (
                field.answers[0] < field.acceptable.value?.split('|')[0] ||
                field.answers[0] > field.acceptable.value?.split('|')[1]
              ) {
                if (field.comment_text?.length > 0) {
                  a.push(field?.label);
                } else {
                  toast.error('FSII comment field is required');
                  s.push(false);
                }
              }
            }
          }
        } else {
          if (field?.data_type !== 'flow_rate') {
            if (isRequired) {
              if (
                field?.answers.length === 0 ||
                field?.answers[0]?.length === 0
              ) {
                toast.error(field?.label + ' required');
                s.push(false);
              } else {
                a.push(field?.label);
              }
            }
          } else {
            a.push(field?.label);
          }
        }
      });
    }

    const validationPassed = s.length === 0;
    if (!validationPassed) return;

    if (!skipGravityValidation) {
      const gravityField = sendData?.items[0]?.fields?.find(
        item => item?.data_type === 'measured_gravity_minus_reported',
      );
      const measuredGravityMinusReported = gravityField?.answers?.[0];

      if (
        measuredGravityMinusReported != null &&
        (Number(measuredGravityMinusReported) < -1 ||
          Number(measuredGravityMinusReported) > 1)
      ) {
        setGravityOutOfRangeModal(true);
        return;
      }
    }

    if (!skipOosValidation) {
      // The answers are read from the store rather than from `sendData`, which
      // is built from the fields the backend sent and so carries neither the
      // acceptable range derived from the fluid brand nor the latest answers.
      const outOfServiceFields = getOutOfServiceFields(categoryFields);

      if (outOfServiceFields.length > 0) {
        setOosWarningFields(outOfServiceFields);
        setOosWarningModal(true);
        return;
      }
    }

    if (s.length === 0) {
      const newSendData = JSON.parse(JSON.stringify(sendData));
      const newSendFields = newSendData?.items[0]?.fields;
      newSendFields?.map(field => {
        // A `required_when` field whose condition is not met and that was
        // left empty is sent with no answers, never [null] or [''].
        if (
          parseRequiredWhen(field?.required_when) &&
          !isRequiredWhenMet(field, newSendFields) &&
          !isFieldFilled(field)
        ) {
          field.answers = [];
          return field;
        }
        if (field?.optional === true || field?.hidden === true) {
          if (field?.answers?.length === 0) {
            field.answers = [''];
          }
        }
        return field;
      });

      let startTimeStamp = startTime?.getTime();
      newSendData.items[0].start_timestamp = String(startTimeStamp)?.slice(
        0,
        10,
      );
      let endTime = new Date()?.getTime();
      newSendData.items[0].end_timestamp = String(endTime)?.slice(0, 10);
      dispatch(setLoader(true));
      setLocalLoaderStatus(true);

      let fieldWithChildCategory = sendData?.items[0]?.fields?.filter(
        item => item.child_category,
      )[0];
      if (
        fieldWithChildCategory?.answers[0] ===
        fieldWithChildCategory?.child_category?.answer
      ) {
        setShowCategories(old => [
          ...old.map(item => {
            fieldWithChildCategory?.child_category?.code?.map(code => {
              if (item.category_code === code) {
                item.is_hidden = false;
              }
            });
            return item;
          }),
        ]);
      }

      try {
        // Save inspection and wait for backend response
        const res = await axios.post(
          `${import.meta.env.VITE_API_URL}inspection/save`,
          newSendData,
          {headers: getInspectionRequestHeaders()},
        );

        // Work with categories based on latest data
        let categories = data?.categories ?? [];

        // Only refresh inspection data when save was successful
        if (res?.status === 200) {
          try {
            const refreshed = await getInspectionById(params?.id).unwrap();
            categories = refreshed?.categories ?? categories;

            const targetIndex =
              currentIndex !== null && currentIndex !== undefined
                ? currentIndex
                : 0;
            const completedCategory =
              categories.find(c => c?.id === currentItem?.id) ??
              categories[targetIndex];
            const completedCategoryFields = (
              completedCategory?.fields ?? []
            ).filter(el => {
              if (el?.data_type === 'flow_rate' && el?.hidden) return true;
              return !el?.hidden;
            });
            dispatch(
              setFields({
                fields: completedCategoryFields,
                type: 'full',
                category_id: completedCategory?.id,
              }),
            );
          } catch (err) {
            console.log(err?.data);
          }

          toast.success(res.data.message, 500);
        }

        const isGracePeriodSubmit = extractGracePeriodSubmitFlag(res?.data);
        if (isGracePeriodSubmit) {
          const gracePeriodSubmitDateRange =
            extractGracePeriodSubmitDateRange(res?.data);
          const effectiveMinDate =
            gracePeriodSubmitDateRange?.start || manualCompletionMinDate;
          const effectiveMaxDate =
            gracePeriodSubmitDateRange?.end || manualCompletionMaxDate;
          const defaultManualCompletionDate = clampInputDateTime(
            toInputDateTime(data?.date) || effectiveMinDate || effectiveMaxDate,
            effectiveMinDate,
            effectiveMaxDate,
          );

          setPendingCategoriesAfterSave(categories);
          setManualCompletionDateRange(
            gracePeriodSubmitDateRange || {start: '', end: ''},
          );
          setManualCompletionDate(defaultManualCompletionDate);
          setManualCompletionDateError('');
          setManualCompletionComment('');
          setManualCompletionModal(true);
          dispatch(setLoader(false));
          setLocalLoaderStatus(false);
          return;
        }

        continueAfterSave(categories);
        // stop local skeleton loader once all success logic is done
        setLocalLoaderStatus(false);
      } catch (err) {
        dispatch(setLoader(false));
        setLocalLoaderStatus(false);
        if (err?.response?.data?.comment_text?.length > 0) {
          err?.response?.data?.comment_text?.forEach(el => {
            toast.error(el, 1000);
          });
        }
      }
    }
  };

  const onCloseGravityOutOfRangeModal = () => {
    setGravityOutOfRangeModal(false);
    dispatch(clearGravityFields());
    setFieldsRenderKey(k => k + 1);
  };

  const onCreateCorrectiveAction = () => {
    setGravityOutOfRangeModal(false);
    completeInspection({skipGravityValidation: true});
  };

  const oosCopy = key => {
    const translationKey = `fillInspection.oosWarningModal.${key}`;
    const translated = t(translationKey);
    return !translated || translated === translationKey
      ? (OOS_FALLBACK[language] ?? OOS_FALLBACK.en)[key]
      : translated;
  };

  const onCloseOosWarningModal = () => {
    setOosWarningModal(false);
    setOosWarningFields([]);
  };

  const onConfirmOosWarning = () => {
    setOosWarningModal(false);
    completeInspection({skipGravityValidation: true, skipOosValidation: true});
  };

  const hasCompletedCategoryChanges = useMemo(() => {
    if (!currentItem?.is_completed) return true;

    const normalizeValue = value => {
      if (Array.isArray(value)) return value;
      if (value === undefined || value === null) return [];
      return [value];
    };

    const normalizeFields = sourceFields =>
      (sourceFields ?? [])
        .map(field => ({
          id: field?.id,
          answers: normalizeValue(field?.answers),
          comment_text: field?.comment_text ?? '',
          comment_files: normalizeValue(field?.comment_files),
          photo: field?.photo ?? null,
          photos: normalizeValue(field?.photos),
        }))
        .sort((a, b) => (a.id > b.id ? 1 : -1));

    const initialSnapshot = JSON.stringify(normalizeFields(currentItem?.fields));
    const currentSnapshot = JSON.stringify(
      normalizeFields(categoryFields ?? currentItem?.fields),
    );

    return initialSnapshot !== currentSnapshot;
  }, [currentItem, categoryFields]);

  return (
    <Wrapper>
      <Helmet>
        <title>Inspection Fill | NDX</title>
      </Helmet>
      <DashboardHeader
        title={data?.inspection_name}
        showBtn={true}
        type={
          data?.period === 'daily' || data?.period === 'weekly'
            ? data?.is_complete
              ? ''
              : 'inspection_fill_skip'
            : 'inspection_fill'
        }
        inspection_type={data?.period}
        action={() => setSkipModal(true)}
        bottomElement={<HeaderBottom inspection={data} />}
      />
      {commentsStatus && (
        <CommentsInspections
          inspectionId={params?.id}
          data={getCommentsData}
          getCommentsAction={getCommentsAction}
          exit={() => setCommentsStatus(false)}
        />
      )}
      {skipModal && (
        <div className='modal'>
          <div className='modal__content'>
            <div className='modal__content-head'>
              <p>Skip Inspections</p>
              <img
                className='modal__content-exit'
                src='/assets/icons/close.svg'
                alt='close'
                onClick={() => setSkipModal(false)}
              />
            </div>
            <div
              className='modal__content-body'
              style={{display: 'flex', justifyContent: 'center'}}
            >
              Are you sure want to Skip Inspections?
            </div>
            <div className='modal__content-bottom'>
              <button className='close' onClick={() => setSkipModal(false)}>
                Cancel
              </button>
              <button
                className='save danger'
                onClick={() => {
                  skipInspection(data?.inspection_id);
                }}
              >
                Skip
              </button>
            </div>
          </div>
          <div className='modal__bg'></div>
        </div>
      )}
      {gravityOutOfRangeModal && (
        <div className='modal'>
          <div className='modal__content'>
            <div className='modal__content-head'>
              <p>{t('fillInspection.gravityOutOfRangeModal.title')}</p>
              <img
                className='modal__content-exit'
                src='/assets/icons/close.svg'
                alt='close'
                onClick={() => setGravityOutOfRangeModal(false)}
              />
            </div>
            <div
              className='modal__content-body'
              style={{display: 'flex', justifyContent: 'center'}}
            >
              {t('fillInspection.gravityOutOfRangeModal.message')}
            </div>
            <div className='modal__content-bottom gravity-out-of-range-modal__buttons'>
              <button className='close' onClick={onCloseGravityOutOfRangeModal}>
                {t('fillInspection.goBack')}
              </button>
              <button className='save' onClick={onCreateCorrectiveAction}>
                {t('fillInspection.createCorrectiveAction')}
              </button>
            </div>
          </div>
          <div className='modal__bg'></div>
        </div>
      )}
      {oosWarningModal && (
        <div className='modal'>
          <div className='modal__content'>
            <div className='modal__content-head'>
              <p>{oosCopy('title')}</p>
              <img
                className='modal__content-exit'
                src='/assets/icons/close.svg'
                alt='close'
                onClick={onCloseOosWarningModal}
              />
            </div>
            <div className='modal__content-body'>
              <p>{oosCopy('message')}</p>
              <ul className='fillInspection__oosWarning-list'>
                {oosWarningFields.map(field => (
                  <li key={field?.id}>
                    {field?.label}: {field?.answers?.[0]} (
                    {describeAcceptable(parseAcceptable(field?.acceptable))})
                  </li>
                ))}
              </ul>
            </div>
            <div className='modal__content-bottom gravity-out-of-range-modal__buttons'>
              <button className='close' onClick={onCloseOosWarningModal}>
                {oosCopy('goBack')}
              </button>
              <button className='save danger' onClick={onConfirmOosWarning}>
                {oosCopy('confirm')}
              </button>
            </div>
          </div>
          <div className='modal__bg'></div>
        </div>
      )}
      {saveAndContinueConfirmModal && (
        <div className='modal'>
          <div className='modal__content'>
            <div className='modal__content-head'>
              <p>Save and Continue</p>
              <img
                className='modal__content-exit'
                src='/assets/icons/close.svg'
                alt='close'
                onClick={() => setSaveAndContinueConfirmModal(false)}
              />
            </div>
            <div
              className='modal__content-body'
              style={{display: 'flex', justifyContent: 'center'}}
            >
              Are you sure you want to change the values of these categories?
            </div>
            <div className='modal__content-bottom'>
              <button
                className='close'
                onClick={() => setSaveAndContinueConfirmModal(false)}
              >
                Cancel
              </button>
              <button
                className='save'
                onClick={() => {
                  setSaveAndContinueConfirmModal(false);
                  completeInspection();
                }}
              >
                Continue
              </button>
            </div>
          </div>
          <div className='modal__bg'></div>
        </div>
      )}
      {manualCompletionModal && (
        <div className='modal'>
          <div className='modal__content'>
            <div className='modal__content-head'>
              <p>{t('fillInspection.manualCompletion.title')}</p>
              <img
                className='modal__content-exit'
                src='/assets/icons/close.svg'
                alt='close'
                onClick={() => {
                  setManualCompletionModal(false);
                  setManualCompletionDateError('');
                  setManualCompletionDateRange({start: '', end: ''});
                }}
              />
            </div>
            <div className='modal__content-body fillInspection__manual-completion'>
              <p className='fillInspection__manual-completion-note'>
                {t('fillInspection.manualCompletion.message')}
              </p>
              <div className='fillInspection__manual-completion-field'>
                <label>{t('fillInspection.manualCompletion.dateLabel')}</label>
                <input
                  type='datetime-local'
                  value={manualCompletionDate}
                  min={manualCompletionMinDate || undefined}
                  max={manualCompletionMaxDate || undefined}
                  onChange={e => onManualCompletionDateChange(e.target.value)}
                />
                {(manualCompletionDateError ||
                  manualCompletionDateRangeMessage) && (
                  <p
                    className={`fillInspection__manual-completion-date-message ${
                      manualCompletionDateError
                        ? 'fillInspection__manual-completion-date-message_error'
                        : ''
                    }`}
                  >
                    {manualCompletionDateError
                      ? `${manualCompletionDateError}${
                          manualCompletionDateRangeMessage
                            ? `. ${manualCompletionDateRangeMessage}`
                            : ''
                        }`
                      : manualCompletionDateRangeMessage}
                  </p>
                )}
              </div>
              <div className='fillInspection__manual-completion-field'>
                <label>{t('fillInspection.manualCompletion.commentLabel')}</label>
                <textarea
                  value={manualCompletionComment}
                  onChange={e => setManualCompletionComment(e.target.value)}
                  placeholder={t('fillInspection.manualCompletion.commentPlaceholder')}
                  rows={4}
                />
              </div>
            </div>
            <div className='modal__content-bottom'>
              <button
                className='save'
                onClick={submitManualCompletedDate}
                disabled={manualCompletionSubmitting}
              >
                {manualCompletionSubmitting
                  ? t('fillInspection.manualCompletion.submitting')
                  : t('fillInspection.manualCompletion.submit')}
              </button>
            </div>
          </div>
          <div className='modal__bg'></div>
        </div>
      )}
      <div className='fillInspection'>
        <div className='fillInspection__left'>
          {getCommentsData && (
            <div
              className='fillInspection__comments'
              onClick={() => setCommentsStatus(true)}
            >
              <CommentsIcon />
              <p>Remarks {`(${getCommentsData?.length})`}</p>
            </div>
          )}
          <div className='fillInspection__counter'>
            <p className='fillInspection__counter-text'>
              {t('fillInspection.completed')}:
            </p>
            <p className='fillInspection__counter-text'>{data?.status}</p>
          </div>
          <div className='fillInspection__list'>{renderCategories()}</div>
        </div>
        {data?.is_skipped && data?.is_shifted_placeholder && (
          <div className='fillInspection__right'>
            <div className='fillInspection__fields'>
              <div className='fillInspection__right-name'>
                {currentItem?.label}
              </div>
            </div>
            <div className='fillInspection__skip'>
              <img src='/assets/skip-inspections.svg' alt='skip' />
              <p className='fillInspection__skip-text'>
                Inspection Not Recorded
              </p>
            </div>
          </div>
        )}

        {data?.is_skipped && !data?.is_shifted_placeholder && (
          <div className='fillInspection__right'>
            <div className='fillInspection__fields'>
              <div className='fillInspection__right-name'>
                {currentItem?.label}
              </div>
            </div>
            <div className='fillInspection__skip'>
              <img src='/assets/skip-inspections.svg' alt='skip' />
              <p className='fillInspection__skip-text'>
                Inspections were skipped and assigned to NOT USED status
              </p>
            </div>
          </div>
        )}

        {currentItem?.fields?.length > 0 && (
          <div className='fillInspection__right' key={fieldsRenderKey}>
            <div className='fillInspection__fields'>
              <div className='fillInspection__right-name'>
                <p>{currentItem?.label}</p>
                {currentItem?.has_history && (
                  <button
                    type='button'
                    className='fillInspection__logs-btn'
                    onClick={() => setLogsModal(true)}
                  >
                    <img
                      src={listIcon}
                      alt=''
                      className='fillInspection__logs-btn-icon'
                    />
                    <span>Logs</span>
                  </button>
                )}
              </div>
              {sameIdsError && (
                <div className='fillInspection__fields-error'>
                  Fields have the same ids, this category may not be completed
                </div>
              )}
              {localLoaderStatus ? (
                <div>
                  <span className='skeleton-loader'></span>
                </div>
              ) : (
                renderFields()
              )}
            </div>
            { location?.state?.readOnly || currentItem?.is_readonly ? (
              // location?.state?.readOnly  ?
              <p className='fillInspection__unable'>
                Unable to answer the question because the grace period expired
                on {data?.grace_period_date}
              </p>
            ) : location?.state?.expired ? (
              <p className='fillInspection__unable'>
                Unable to answer the question because the grace period of{' '}
                {data?.grace_period_days}{' '}
                {data?.grace_period_days === 1 ? 'day' : 'days'} has expired.
              </p>
            ) : (
              <div className='fillInspection__btns'>
                { data?.is_complete || !location?.state?.sheet && (
                  <div
                    className='fillInspection__btn fillInspection__btn_close'
                    onClick={() => {
                      navigate(-1);
                    }}
                  >
                    {t('fillInspection.cancel')}
                  </div>
                )}
                { data?.is_complete || !location?.state?.sheet &&
                  !sameIdsError &&
                  !data?.disabled_by_grace_period && (
                    <div
                      onClick={() => {
                        if (
                          !data?.disabled_by_grace_period &&
                          hasCompletedCategoryChanges
                        ) {
                          if (currentItem?.is_completed) {
                            setSaveAndContinueConfirmModal(true);
                          } else {
                            completeInspection();
                          }
                        }
                      }}
                      className={`fillInspection__btn ${
                        data?.disabled_by_grace_period ||
                        !hasCompletedCategoryChanges
                          ? 'disabled'
                          : ''
                      }`}
                      disabled={
                        data?.disabled_by_grace_period ||
                        !hasCompletedCategoryChanges
                      }
                    >
                      {location.search
                        ? t('fillInspection.updateInspection')
                        : lastCategory
                          ? t('fillInspection.completeInspection')
                          : t('fillInspection.saveAndContinue')}
                    </div>
                  )}
              </div>
            )}
          </div>
        )}
      </div>
      {logsModal && (
        <LogsModal
          currentItem={currentItem}
          onClose={() => setLogsModal(false)}
        />
      )}
      {correctiveActionsModal && (
        <CorrectiveActionsModal
          correctiveActions={currentCorrectiveActions}
          currentStep={correctiveActionStep}
          setCurrentStep={setCorrectiveActionStep}
          inspection={currentItem}
          setInspection={setCurrentItem}
          onClose={() => {
            // Mark all current corrective actions as viewed
            setViewedCorrectiveActionIds(prevSet => {
              const newSet = new Set(prevSet);
              currentCorrectiveActions.forEach(action => {
                if (action.id) {
                  newSet.add(action.id);
                }
              });
              return newSet;
            });

            setCorrectiveActionsModal(false);
            setCurrentCorrectiveActions([]);
            // Keep current category after refetch (don't jump back to first)
            preserveCategoryOnNextFetchRef.current = true;
            setRequestSended(false);
            setCorrectiveActionStep(1);
          }}
        />
      )}
    </Wrapper>
  );
}
